import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BodyOf, Messages } from "../../../shared/api";
import { ModalContentError, ModalContentLoading } from "../../../shared/components/Modal";
import { WorkflowTagId } from "../../../shared/schema/schema";
import { createDisclosureTriggerComponent } from "../../../shared/utils/create-disclosure-trigger-component";
import { useApi } from "../../../shared/utils/use-api";
import useTagForm from "../hooks/useTagForm";
import { zForm } from "../tag.const";
import TagFormControls from "./TagFormControls";

type Props = {
  id: WorkflowTagId;
  disclosure: ReturnType<typeof useDisclosure>;
};

export default function EditTagModal(props: Props) {
  return (
    <Modal {...props.disclosure} isCentered={true}>
      <ModalOverlay />
      <EditTagModalContent disclosure={props.disclosure} id={props.id} />
    </Modal>
  );
}

export const EditTagModalTrigger = createDisclosureTriggerComponent<{
  id: WorkflowTagId;
}>({
  displayName: "EditTagModalTrigger",
  render: EditTagModal,
});

function EditTagModalContent(props: Props) {
  const query = useQuery(useApi().queries.tag(props.id));

  switch (query.status) {
    case "loading":
      return <ModalContentLoading />;
    case "error":
      return <ModalContentError error={query.error} />;
    case "success":
      return <ModalContentSuccess disclosure={props.disclosure} tag={query.data} />;
  }
}

function ModalContentSuccess(props: {
  tag: Messages["ListedWorkflowTag"];
  disclosure: ReturnType<typeof useDisclosure>;
}) {
  const toast = useToast();
  const { api, queries } = useApi();
  const queryClient = useQueryClient();

  const form = useTagForm({
    defaultValues: {
      id: props.tag.id,
      input: props.tag.input,
      name: props.tag.name,
    },
  });

  const mutation = useMutation({
    mutationFn: (body: BodyOf<"put", "./workflow_tags/:workflowTagId">) => {
      return api.put("./workflow_tags/:workflowTagId", {
        body,
        path: { workflowTagId: props.tag.id },
      });
    },
    onSuccess: () => {
      toast({ position: "top", description: "Tag updated", status: "success" });
      queryClient.invalidateQueries(queries.tags().queryKey);
      queryClient.invalidateQueries(queries.tag(props.tag.id).queryKey);
      props.disclosure.onClose();
    },
    onError: (error) => {
      toast({ position: "top", description: `${error}`, status: "error" });
    },
  });

  const handleSuccess = (data: z.infer<typeof zForm>) => {
    mutation.mutate({
      name: data.name,
    });
  };
  return (
    <ModalContent as="form" onSubmit={form.handleSubmit(handleSuccess, console.log)}>
      <ModalHeader>Edit Tag</ModalHeader>
      <ModalCloseButton />

      <ModalBody>
        <TagFormControls form={form} mode="edit" />
      </ModalBody>
      <ModalFooter gap={3}>
        <Button variant="ghost" onClick={props.disclosure.onClose}>
          Cancel
        </Button>
        <Button colorScheme="blue" type="submit">
          Submit
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}
